<!-- =====================================================================
     Sidebar — TWS-108 Wave 4 / Wave 8 (Portal Chrome Restyle)
     ---------------------------------------------------------------------
     `.tw-sidebar` is the chrome; grid-area + sticky come from the
     `.tw-layout__sidebar` host binding on <app-sidebar-nav>. The mobile
     slide-over dialog is Wave 6; this template renders the full + rail
     modes only (hidden state collapses the grid column to 0 via
     `.tw-layout[data-sidebar="hidden"]`).

     Wave 8 retired the bespoke rail-mode flyout (cdkConnectedOverlay +
     popoutHover timer machinery). Each group-parent now hosts its own
     `<tw-popover>` with `triggerOn="hover"` and the trigger directive's
     `enabled` input gated on `flyoutEnabled(collapsed)` (rail OR
     collapsed-group full mode). Leaf rail items consume `[twTooltip]`
     for the label-only hint.
     ==================================================================== -->
<aside class="tw-sidebar"
       aria-label="Primary navigation">

  <!-- Brand header — matches topbar height (64 px). Full wordmark when
       expanded, mark-only glyph in rail. Both SVGs use currentColor. -->
  <a routerLink="/"
     class="tw-sidebar__brand"
     aria-label="Twist home">
    @if (isRail()) {
      <svg class="tw-sidebar__logo-mark"
           viewBox="0 0 45 100"
           xmlns="http://www.w3.org/2000/svg"
           aria-hidden="true"
           focusable="false">
        <path fill-rule="evenodd"
              fill="currentColor"
              d="M14.5,86.8L14.5,86.8l5.8-0.1L18,99l10.4-0.4l2.4-11.9l5.9-0.2l2-10.1l-5.9,0.2L44,20.3c1.2-6-0.7-8.1-6.6-7.9l-9.3,0.3l0,0h-4.6L25.8,1H15.4l-2.2,11.8H6.8l-1.4,10h5.9L0.9,78.7c-1.1,6,0.8,8.1,6.7,8.1H14.5z M27.2,22.8h0.5l0,0l4.4-0.2c1,0,1.2,0.3,1,1.3L22.3,76.7L19,76.8h-6c-1,0-1.2-0.3-1-1.3l9.8-52.8h4.4l0,0H27.2z"/>
      </svg>
    } @else {
      <svg class="tw-sidebar__logo-full"
           viewBox="0 0 100 58"
           xmlns="http://www.w3.org/2000/svg"
           aria-hidden="true"
           focusable="false">
        <g fill="currentColor">
          <path d="M60.3,39.8l-1.2,5.4v0c-1.9,8.6,0,12.1,6.2,12.1c6.1,0,10-3.5,11.9-12.1l0.1-0.6c1.8-7.8,0.1-12.9-1.5-17.7c-1.4-4.3-2.8-8.4-1.5-14l0.6-2.8c0.3-1.4,0.8-1.9,1.6-1.9c0.7,0,1,0.5,0.7,1.9l-1.5,8.1h7.6l1.2-5.4c1.9-8.6,0-12.1-6.2-12.1c-6.1,0-10,3.5-11.9,12.1l-0.1,0.6c-1.8,7.8-0.1,12.9,1.5,17.7c1.4,4.3,2.8,8.4,1.5,14L68.7,48c-0.3,1.4-0.8,1.9-1.6,1.9c-0.7,0-1-0.5-0.7-1.9l1.5-8.1H60.3z"/>
          <path d="M79.1,42.8H83l8.3-34.4c0.2-0.6,0-0.8-0.6-0.8h-3.9L88.4,1h6.1c3.9,0,5.1,1.4,4.2,5.3l-8.8,36.4h3.9l-1.6,6.5h-3.9L86.5,57h-6.9l1.9-7.7h-3.9L79.1,42.8L79.1,42.8z"/>
          <path d="M18.9,15.2H15L8.5,49.6c-0.1,0.6,0.1,0.8,0.7,0.8h4.2L12.1,57H5.7c-3.9,0-5.2-1.4-4.5-5.3l6.9-36.5H4.1L5,8.7h4.2L10.7,1h6.9l-1.5,7.7h3.9L18.9,15.2z"/>
          <path d="M23.5,1.3l-8.8,55.4h12.6l7.3-32.8H36l-5.1,32.8h12.6L56.1,1.3h-7.6l-8.4,42.5h-1.7l6.2-42.5h-9.7l-9.8,42.5h-1.8L31,1.3H23.5z"/>
          <path d="M46.5,56.7l12-55.4h7.9l-12,55.4H46.5z"/>
        </g>
      </svg>
    }
  </a>

  <!-- Nav scroll region — `.tw-sidebar__nav` owns overflow-y: auto.
       The rail-mode flyout escapes via the `<tw-popover>` top-layer
       (native [popover]) so `overflow-x` no longer matters. -->
  <nav class="tw-sidebar__nav" aria-label="Primary">
    <!-- Registry-composed sections — the SOLE source of sidebar content
         (the static sideNavConfig + legacy fallback were retired in Phase
         4). Items are grouped by their owning descriptor's `group` and
         each keeps its own `*authGuard` (descriptor.security), so items the
         user can't access never enter the DOM. -->
    @for (section of composedSections(); track section.title) {
      <ng-container *ngTemplateOutlet="sectionTpl; context: { $implicit: section }"></ng-container>
    }

    <!-- Reserved "Pinned" personalisation slot. Renders nothing while
         empty (no PinnedNavSource yet) — holds the layout position for a
         future wave. Tagged `reserved` so the "hide empty group" CSS rule
         (.tw-sidebar__group:not(.tw-sidebar__group--reserved):has-no-rows)
         never collapses a deliberately-reserved placeholder once it does
         render with items. -->
    @if (pinnedSection().items.length > 0) {
      <ng-container *ngTemplateOutlet="sectionTpl; context: { $implicit: pinnedSection(), reserved: true }"></ng-container>
    }
  </nav>

  <!-- Shared section template — one definition, instantiated per
       section. `let-section` binds the section object passed via the
       template-outlet context `$implicit`. -->
  <ng-template #sectionTpl let-section let-reserved="reserved">
      <!-- A registry section enters the DOM with >=1 item, but every item
           sits behind its own async `*authGuard` (descriptor.security). When
           ALL of them resolve to "no access" the group is left with only its
           header. The `:has()`-based rule in _sidebar.scss collapses any
           non-reserved group that ends up with zero rendered
           `.tw-sidebar__item-row` children, so an empty group shows nothing —
           no header, no wrapper box. `--reserved` opts the Pinned slot OUT of
           that collapse. -->
      <div class="tw-sidebar__group"
           [class.tw-sidebar__group--reserved]="reserved"
           *authGuard="section.securityConstraints">
        <div class="tw-sidebar__group-label" aria-hidden="true">{{ section.title }}</div>

        @for (topLevelItem of section.items; track topLevelItem.title) {
          @let itemKey = section.title + '_' + topLevelItem.title;
          @let collapsed = isCollapsed(itemKey)();
          @let flyoutOn = flyoutEnabled(collapsed);

          @if (hasSubItems(topLevelItem)) {
            <!-- Group parent — click expands subnav in full mode; in rail
                 (or when collapsed in full mode) the wrapping
                 `<tw-popover>` opens a hover flyout that shows the group's
                 sub-items. The popover primitive owns hover/focus state +
                 timers, top-layer presentation, and Escape dismissal. -->
            <tw-popover triggerOn="hover" placement="end"
                        *authGuard="topLevelItem.securityConstraints">
              <div class="tw-sidebar__item-row">
                <button type="button"
                        twPopoverTrigger
                        [enabled]="flyoutOn"
                        class="tw-sidebar__item"
                        [class.tw-sidebar__item--active]="hasActiveChild(topLevelItem)"
                        [class.tw-sidebar__item--open]="!isRail() && !collapsed"
                        [attr.aria-expanded]="isRail() ? null : !collapsed"
                        [attr.aria-controls]="itemKey"
                        (click)="toggleCollapse(itemKey)">
                  <span class="tw-sidebar__icon">
                    <ui-svg-icon [icon]="asSvgIcon(topLevelItem.icon)" size="normal"></ui-svg-icon>
                  </span>
                  <span class="tw-sidebar__label">{{ topLevelItem.title }}</span>
                  @if (!isRail()) {
                    <!-- Inline stroked chev matching `icons.chev` in the handoff
                         icon set. Renders only in full-sidebar mode. -->
                    <span class="tw-sidebar__chev" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
                           stroke="currentColor" stroke-width="1.6"
                           stroke-linecap="round" stroke-linejoin="round"
                           focusable="false">
                        <path d="m8 5 4 5-4 5"/>
                      </svg>
                    </span>
                  }
                </button>

                <div class="tw-sidebar__collapse"
                     [id]="itemKey"
                     [attr.aria-hidden]="collapsed"
                     [class.tw-sidebar__collapse--hidden]="collapsed">
                  <ul class="tw-sidebar__subnav" role="group" [attr.aria-label]="topLevelItem.title">
                    @for (subItem of topLevelItem.subItems; track subItem.routerLink) {
                      <li *authGuard="subItem.securityConstraints">
                        <a class="tw-sidebar__subitem"
                           routerLinkActive="tw-sidebar__subitem--active"
                           [routerLink]="subItem.routerLink"
                           [attr.aria-current]="isActiveLink(subItem.routerLink) ? 'page' : null">
                          {{ subItem.title }}
                        </a>
                      </li>
                    }
                  </ul>
                </div>
              </div>

              <!-- Rail-mode (or collapsed full-mode) flyout. Per-instance
                   sizing matches the Wave 4 design (280-320px wide); padding
                   is zeroed so the head + items list own section layout. -->
              <tw-popover-content
                class="tw-sidebar-flyout"
                style="--twist-popover-min-inline-size: 280px; --twist-popover-max-inline-size: 320px; --twist-popover-padding: 8px;"
                [attr.aria-label]="topLevelItem.title">
                <div class="tw-sidebar-flyout__head">
                  <div class="tw-sidebar-flyout__head-text">
                    <div class="tw-sidebar-flyout__head-title">{{ topLevelItem.title }}</div>
                    @if (topLevelItem.description) {
                      <div class="tw-sidebar-flyout__head-sub">{{ topLevelItem.description }}</div>
                    }
                  </div>
                </div>
                <ul class="tw-sidebar-flyout__list">
                  @for (subItem of topLevelItem.subItems; track subItem.routerLink) {
                    <li *authGuard="subItem.securityConstraints">
                      <a class="tw-sidebar-flyout__item"
                         routerLinkActive="tw-sidebar-flyout__item--active"
                         [routerLink]="subItem.routerLink"
                         [attr.aria-current]="isActiveLink(subItem.routerLink) ? 'page' : null">
                        <span class="tw-sidebar-flyout__item-body">
                          <span class="tw-sidebar-flyout__item-label">{{ subItem.title }}</span>
                          @if (subItem.description) {
                            <span class="tw-sidebar-flyout__item-desc">{{ subItem.description }}</span>
                          }
                        </span>
                        <span class="tw-sidebar-flyout__item-chev" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none"
                               stroke="currentColor" stroke-width="1.6"
                               stroke-linecap="round" stroke-linejoin="round"
                               focusable="false">
                            <path d="m8 5 4 5-4 5"/>
                          </svg>
                        </span>
                      </a>
                    </li>
                  }
                </ul>
              </tw-popover-content>
            </tw-popover>
          } @else {
            <!-- Leaf top-level. Three render modes:
                 - disabled (registry only): greyed, aria-disabled, NOT a
                   link — visible but non-navigable.
                 - external (registry only): plain anchor target=_blank
                   rel=noopener + an external-link affordance icon.
                 - default: routerLink (legacy + internal registry items).
                 In rail mode a label-only tooltip supplies the hover hint.
                 The per-item `*authGuard` (descriptor.security) gates leaf
                 visibility exactly as the group-parent + sub-item branches
                 already do; a leaf the user can't access never enters the
                 DOM, which is also what lets an all-filtered section collapse
                 to nothing via the empty-group CSS rule. -->
            <div class="tw-sidebar__item-row" *authGuard="topLevelItem.securityConstraints">
              @if (topLevelItem.disabled) {
                <span class="tw-sidebar__item tw-sidebar__item--disabled"
                      aria-disabled="true"
                      [twTooltip]="topLevelItem.title"
                      twTooltipPlacement="end"
                      [twTooltipDisabled]="!isRail()">
                  <ng-container *ngTemplateOutlet="leafBody; context: { $implicit: topLevelItem }"></ng-container>
                </span>
              } @else if (topLevelItem.external) {
                <a class="tw-sidebar__item tw-sidebar__item--external"
                   [href]="topLevelItem.routerLink"
                   target="_blank"
                   rel="noopener noreferrer"
                   [twTooltip]="topLevelItem.title"
                   twTooltipPlacement="end"
                   [twTooltipDisabled]="!isRail()">
                  <ng-container *ngTemplateOutlet="leafBody; context: { $implicit: topLevelItem }"></ng-container>
                  <!-- External-link affordance icon (decorative; the anchor
                       label already conveys the destination). -->
                  <span class="tw-sidebar__external-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none"
                         stroke="currentColor" stroke-width="1.6"
                         stroke-linecap="round" stroke-linejoin="round"
                         focusable="false">
                      <path d="M11 3h6v6"/>
                      <path d="M17 3l-8 8"/>
                      <path d="M15 12v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4"/>
                    </svg>
                  </span>
                </a>
              } @else {
                <!-- Registry-backed items (navId set) drive the active
                     highlight SOLELY via `isRegistryItemActive`, which
                     combines the active-trail navId match (deep/detail
                     routes like `/installations/:id/dashboard`) with an
                     exact-link fallback (the shallow `/installations`
                     case + first paint before NavigationEnd). We do NOT
                     also bind `routerLinkActive` for this class: the
                     directive re-applies on every router event and would
                     clobber the `[class.…--active]` binding on deep
                     routes (routerLinkActive {exact} is false there),
                     stripping the highlight the navId trail had set.
                     Legacy items (navId undefined) fall back to the
                     exact-link check inside `isRegistryItemActive`. -->
                <a class="tw-sidebar__item"
                   [class.tw-sidebar__item--active]="isRegistryItemActive(topLevelItem)"
                   [routerLink]="topLevelItem.routerLink"
                   [attr.aria-current]="isRegistryItemActive(topLevelItem) ? 'page' : null"
                   [twTooltip]="topLevelItem.title"
                   twTooltipPlacement="end"
                   [twTooltipDisabled]="!isRail()">
                  <ng-container *ngTemplateOutlet="leafBody; context: { $implicit: topLevelItem }"></ng-container>
                </a>
              }
            </div>
          }
        }
      </div>

      <!-- Shared leaf body — icon + label + (registry) badge. Declared
           inside #sectionTpl so its ref is in lexical scope for the three
           leaf render modes (default routerLink, external anchor, disabled
           span) above; keeps the inner markup single-sourced. -->
      <ng-template #leafBody let-topLevelItem>
        <span class="tw-sidebar__icon">
          <ui-svg-icon [icon]="asSvgIcon(topLevelItem.icon)" size="normal"></ui-svg-icon>
        </span>
        <span class="tw-sidebar__label">{{ topLevelItem.title }}</span>
        <!-- Registry leaf badge — static descriptor.badge, or a
             NAV_BADGE_PROVIDER override (reactive) by navId. No group
             aggregation; hidden in rail mode (label hidden). -->
        @if (!isRail()) {
          @let badge = effectiveBadge(topLevelItem);
          @if (badge) {
            <tw-badge
              class="tw-sidebar__badge"
              [type]="badgeType(badge)"
              [status]="badgeStatus(badge)">{{ badgeLabel(badge) }}</tw-badge>
          }
        }
      </ng-template>
  </ng-template>

  <!-- Footer — pulsing status dot + system/version meta.
       The version + uptime strings are hardcoded; feature flag wiring
       lands with the Health endpoint (future epic). -->
  <div class="tw-sidebar__footer">
    <span class="tw-sidebar__status-dot" aria-hidden="true"></span>
    <div class="tw-sidebar__footer-text">
      <div class="tw-sidebar__footer-status">All systems normal</div>
      <div class="tw-sidebar__footer-meta">v24.04.2 · 99.98% uptime</div>
    </div>
  </div>
</aside>
